/**
 * The interface for the app environment.
 */
export interface AppEnvironment {

    /** The list of supported languages provided. */
    supportedLanguages: string[];
    /** 
    /** The current build timesamp */
    buildTimestamp: string;
    /** A flag denoting if the this is a local environment. */
    local: boolean;
    /** A flag denoting if the this is a production environment. */
    production: boolean;
    /** Details partaining to backend API and microservices. */
    api: {
        /** 
         * The base URL for the API backend if all the services have a common 
         * base URL. 
         */
        baseUrl: string | undefined;
        /** 
         * URLs to API backend services that are required through out the app. 
         * Can be relative URLs if no base URL is provided. Also, can house 
         * granular endpoints or even multiple maintained versions. 
         */
        services: {
            question: string;
            response: string;
            launch: string;
            organization: string;
        };
        /**
         * A JWT token that authorizes access to the above services. Only lowest 
         * level of access that allows survey UI to be available to the public 
         * is required (respondent role).
         */
        authToken: string;
    };
    /** Details partaining to Google Analytics service. */
    googleAnalytics: {
        enabled: boolean;
        trackingId: string;
    };
    /** Under maintenance mode configurations. */
    underMaintenance: {
        /** 
         * A flag denoting if the app is under maintenance. Takes more 
         * precedence over setting an activation date. 
         */
        activated: boolean | undefined;
        /** Set an datetime of when under maintenace mode is activated. */
        activationDate: string | undefined; // Format YYYY-MM-DDTHH:mm:ss.sssZ
        /** 
         * Set a headsup statement to notify users that we are soon undergoing 
         * maintenance. 
         */
        headsup: {
            /** The statment as a localized text map. */
            statement: {
                'en-ca': string;
                'fr-ca': string;
                // Add statements from other languages here
            };
            /** For enabling the headsup statement. */
            enabled: boolean | undefined;
        };
    };
};

/**
 * Overrides the passed app environment with corresponding variables in the 
 * user environment.
 * 
 * @param environment The app environment variables
 * @returns The mutated app environment variables
 */
export function overrideWithUserEnvironment(
    appEnv: AppEnvironment, 
    userEnv: Record<string, string | number | boolean> | undefined,
): AppEnvironment {
    /**
     * Recursive helper method.
     * 
     * @param property A property that is a non-primitive type object 
     * https://www.typescriptlang.org/docs/handbook/release-notes/typescript-2-2.html#object-type
     * @param currentPath The current path of the property in the app environment
     */
    function traverse(
        property: object, 
        currentPath: string[], 
        userEnv: Record<string, string | number | boolean>,
    ) {
        for (const key in property) {
            if (property.hasOwnProperty(key)) {
                const value = property[key as keyof typeof property];
                const path = [...currentPath, key];
                if (value !== null && typeof value === 'object') {
                    traverse(value, path, userEnv);
                } else {
                    const userVariableValue = userEnv[`NG_APP_${path.join('_').toUpperCase()}`];
                    if (userVariableValue !== undefined) {
                        // Override the property in the config object
                        property[key as keyof typeof property] = userVariableValue as never;
                        // console.log(`Overriding the environment variable ${path.join('.')} with = ${userVariableValue}`);
                    }
                }
            }
        }
    }

    if (userEnv != undefined) traverse(appEnv, [], userEnv);
    return appEnv;
}
